import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CalendarDays,
  MessageSquare,
  Newspaper,
  Rss,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';

const PREVIEW_TYPES = [
  { labelKey: 'feed.types.news', icon: Newspaper, className: 'bg-blue-50 text-blue-700 border-blue-200' },
  { labelKey: 'feed.preview.clubUpdates', icon: Users, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { labelKey: 'feed.preview.tournamentUpdates', icon: Trophy, className: 'bg-orange-50 text-orange-700 border-orange-200' },
  { labelKey: 'feed.preview.eventActivity', icon: CalendarDays, className: 'bg-lime-50 text-lime-700 border-lime-200' },
  { labelKey: 'feed.filters.announcements', icon: Bell, className: 'bg-amber-50 text-amber-700 border-amber-200' },
];

const GlobalFeedPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#f7f8f4] px-4 py-5 pb-24 lg:px-8 lg:py-8 lg:pb-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="overflow-hidden rounded-[2rem] bg-zinc-950 text-white shadow-xl">
          <div className="relative p-6 sm:p-8">
            <div className="pointer-events-none absolute inset-0 opacity-25">
              <div className="absolute -right-16 top-0 h-64 w-64 rounded-full border border-lime-300/40" />
              <div className="absolute bottom-8 left-10 h-px w-72 bg-lime-300/40" />
              <div className="absolute bottom-8 right-16 h-32 w-72 rounded-[50%] border border-lime-300/20" />
            </div>

            <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-lime-200">
                  <Rss size={14} />
                  {t('feed.tennisFeed')}
                </div>
                <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">
                  {t('feed.globalFeedTitle')}
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-zinc-300">
                  {t('feed.globalFeedDescription')}
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-lime-300 text-zinc-950">
                    <ShieldCheck size={22} />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-white">{t('feed.noUnsupportedActions')}</h2>
                    <p className="mt-1 text-sm leading-6 text-zinc-300">
                      {t('feed.globalPublishingUnavailable')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex gap-2 overflow-x-auto p-1">
            {PREVIEW_TYPES.map(({ labelKey, icon: Icon, className }) => (
              <span
                key={labelKey}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-2xl border px-4 text-sm font-semibold ${className}`}
              >
                <Icon size={16} />
                {t(labelKey)}
              </span>
            ))}
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="rounded-3xl border border-dashed border-zinc-300 bg-white px-6 py-14 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
              <MessageSquare size={30} />
            </div>
            <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{t('feed.noGlobalItems')}</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-zinc-600">
              {t('feed.noGlobalItemsBody')}
            </p>
          </div>

          <aside className="space-y-4">
            <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-lime-100 text-lime-700">
                  <Sparkles size={21} />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-zinc-950">{t('feed.readiness')}</h2>
                  <p className="text-sm text-zinc-500">{t('feed.visualShellOnly')}</p>
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-zinc-600">
                {t('feed.noSamplePostsBody')}
              </p>
            </div>

            <div className="rounded-3xl border border-zinc-200 bg-white p-5 shadow-sm">
              <h2 className="text-base font-semibold text-zinc-950">{t('feed.availableToday')}</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-600">
                {t('feed.availableTodayBody')}
              </p>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};

export default GlobalFeedPage;
